import { CheckCircle2, Mail } from 'lucide-react'
import { Button, Input } from '@/components/atoms'
import { cn } from '@/utils/cn'
import type { EmailDomainClaimResult } from '@/types/organization'
import { SectionHeading } from './FormField'
import { BODY_TEXT, ERROR_TEXT, FIELD_LABEL, HELP_TEXT } from './styles'

export interface DomainClaimValues {
  email_domain: string
  verification_email: string
}

interface DomainClaimStepProps {
  values: DomainClaimValues
  onChange: (updates: Partial<DomainClaimValues>) => void
  /** The claim currently recorded on the server, if any. */
  claim: EmailDomainClaimResult | null
  onClaim: () => void
  claiming: boolean
  error: string | null
  /** The signed-in user's own email — decides whether auto-verification applies. */
  accountEmail: string
  disabled?: boolean
}

const domainOf = (email: string): string => {
  const at = email.lastIndexOf('@')
  return at === -1 ? '' : email.slice(at + 1).trim().toLowerCase()
}

/**
 * Step 3 — claim the entity's email domain.
 *
 * The mechanism the user needs explained, because it decides whether they are
 * finished in one click or waiting on an email:
 *
 *  - **Own email matches the domain → auto-verified, instantly, no email.** This
 *    is the common case (finance ops signing up with their work address) and the
 *    UI says so *before* they submit, so the outcome is never a surprise.
 *  - **Otherwise a challenge is emailed** to an address at the claimed domain,
 *    and the attestation lands in PENDING_VERIFICATION until someone opens it.
 *
 * There is deliberately no DNS TXT option: the person signing up is finance ops
 * and usually has no DNS access.
 */
export const DomainClaimStep = ({
  values,
  onChange,
  claim,
  onClaim,
  claiming,
  error,
  accountEmail,
  disabled,
}: DomainClaimStepProps) => {
  const typedDomain = values.email_domain.trim().toLowerCase().replace(/^@/, '')
  const accountDomain = domainOf(accountEmail)
  const willAutoVerify = typedDomain.length > 0 && typedDomain === accountDomain
  const verified = claim?.email_domain_verified ?? false
  const claimedUnverified = !!claim?.email_domain && !verified

  return (
    <div className="space-y-8">
      <section>
        <SectionHeading
          title="Claim your email domain"
          description="Claiming a domain lets you invite colleagues at that domain without an extra identity check, and shows your organization as domain-verified."
        />

        {verified ? (
          <div className="flex items-start gap-3 border border-[#1fb845]/40 bg-[#1fb845]/10 px-4 py-3">
            <CheckCircle2 size={20} className="mt-[2px] shrink-0 text-[#1fb845]" />
            <div>
              <p className="font-plus-jakarta text-[14px] font-medium text-[#2d2923] dark:text-[#fdfdfd]">
                {claim?.email_domain} is verified
              </p>
              <p className={cn(HELP_TEXT, 'mt-0.5')}>
                {claim?.auto_verified
                  ? 'Your own account email is at this domain, so we verified it immediately — nothing was emailed.'
                  : 'The verification link was redeemed.'}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={FIELD_LABEL} htmlFor="email_domain">
                  Email domain
                </label>
                <Input
                  id="email_domain"
                  value={values.email_domain}
                  onChange={event => onChange({ email_domain: event.target.value })}
                  placeholder="yourcompany.com"
                  disabled={disabled || claiming}
                  className="h-[52px] font-plus-jakarta text-[14px]"
                />
                <p className={cn(HELP_TEXT, 'mt-1')}>
                  Your corporate domain, with no <code>@</code>. Public mailbox providers
                  (gmail.com, outlook.com, …) cannot be claimed.
                </p>
              </div>

              {!willAutoVerify && typedDomain.length > 0 && (
                <div>
                  <label className={FIELD_LABEL} htmlFor="verification_email">
                    Send the verification link to
                  </label>
                  <Input
                    id="verification_email"
                    type="email"
                    value={values.verification_email}
                    onChange={event => onChange({ verification_email: event.target.value })}
                    placeholder={`someone@${typedDomain}`}
                    disabled={disabled || claiming}
                    className="h-[52px] font-plus-jakarta text-[14px]"
                  />
                  <p className={cn(HELP_TEXT, 'mt-1')}>
                    Must be an address at {typedDomain}. Often IT or operations.
                  </p>
                </div>
              )}
            </div>

            {typedDomain.length > 0 && (
              <div className="mt-4 flex items-start gap-3 border border-[rgba(45,41,35,0.12)] bg-[rgba(255,255,255,0.4)] px-4 py-3 dark:border-[rgba(253,253,253,0.12)] dark:bg-[rgba(255,255,255,0.04)]">
                <Mail size={18} className="mt-[2px] shrink-0 text-[rgba(45,41,35,0.56)] dark:text-[rgba(253,253,253,0.56)]" />
                <p className={BODY_TEXT}>
                  {willAutoVerify ? (
                    <>
                      Your account email <strong>{accountEmail}</strong> is at{' '}
                      <strong>{typedDomain}</strong>, so this domain verifies{' '}
                      <strong>immediately</strong> — no email, nothing to wait for.
                    </>
                  ) : (
                    <>
                      Your account email is at <strong>{accountDomain || 'another domain'}</strong>,
                      not <strong>{typedDomain}</strong>, so we will email a verification link and
                      your account stays pending until someone opens it.
                    </>
                  )}
                </p>
              </div>
            )}

            {claimedUnverified && (
              <div className="mt-4 border border-[#ff540c]/40 bg-[#ff540c]/10 px-4 py-3">
                <p className="font-plus-jakarta text-[14px] font-medium text-[#2d2923] dark:text-[#fdfdfd]">
                  {claim?.email_domain} claimed — waiting on verification
                </p>
                <p className={cn(HELP_TEXT, 'mt-0.5')}>
                  {claim?.verification_email_sent
                    ? 'We sent a verification link. Submitting now leaves your account pending until it is opened.'
                    : 'No verification email has gone out yet. Add an address at this domain and claim again.'}
                </p>
              </div>
            )}

            <div className="mt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={onClaim}
                isLoading={claiming}
                disabled={disabled || claiming || typedDomain.length === 0}
              >
                {claimedUnverified ? 'Re-send verification' : 'Claim domain'}
              </Button>
              {/* Below the button, never above it: a failed claim used to insert
                  this line into the gap over the button and push it out from
                  under the pointer that had just clicked it. */}
              {error && <p className={ERROR_TEXT}>{error}</p>}
            </div>
          </>
        )}
      </section>

      <p className={BODY_TEXT}>
        This step is optional. You can finish without claiming a domain and add one later from
        Profile &amp; Settings — membership is invite-only either way, so a verified domain never
        lets anyone join by itself.
      </p>
    </div>
  )
}
